import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Stat,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
  type TagTone,
} from '@dayon/design-system';
import { formatCount, formatPesos } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import {
  PAYOUT_STATUS_LABEL,
  type PayoutDomain,
  type PayoutRow,
  type PayoutRunPayload,
} from './types.ts';

/**
 * The payout run for one settled commission period.
 *
 * ── WHAT THIS IS THE MISSING HALF OF ───────────────────────────────────────
 * The worker calculates a period and stops there on purpose: `commission.close`
 * never marks anything PAID, because a payment needs an approver and a
 * scheduled job is nobody. Until this screen existed there was nothing to carry
 * on with — `agent.agent_payout` and `affiliate.affiliate_payout` had one
 * writer each, the seed, and an affiliate could request an early payout that no
 * operator anywhere could answer.
 *
 * ── THREE STEPS AND TWO PEOPLE ─────────────────────────────────────────────
 *   Raise    one operator turns the period's results into scheduled payouts.
 *            The amounts come from `net_commission` and from nothing typed
 *            here: an operator who could name an amount could pay a partner a
 *            figure their own statement does not carry.
 *   Approve  a DIFFERENT operator approves each one. The database enforces it
 *            (`*_payout_maker_is_not_checker`) and the route refuses first, so
 *            what an operator reads is the rule and not a constraint name.
 *   Settle   and records the transfer that was actually made.
 *
 * ── THE NOTICE THAT HAS TO STAY TRUE ───────────────────────────────────────
 * This platform integrates no payment provider and stubs none. "Record
 * settlement" does not send money to a GCash wallet or a bank; it records a
 * transfer a person made outside this platform, which is why it asks for that
 * transfer's own reference. The API returns the same sentence, so a caller that
 * is not this screen reads it too.
 *
 * ── ONE COMPONENT, TWO DOMAINS ─────────────────────────────────────────────
 * The agent and affiliate commission ENGINES are independent and stay that way:
 * two rule sets, two period tables, two route modules. What is shared here is
 * the chrome — a table of payouts and three buttons — and two 300-line copies
 * of that is two places for the maker–checker wording to drift.
 */
export function PayoutRunDialog({
  domain,
  periodId,
  onClose,
  onDone,
}: {
  domain: PayoutDomain;
  periodId: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { allowed } = useSession();
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [payload, setPayload] = useState<PayoutRunPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Which payout is being answered, and how. */
  const [answering, setAnswering] = useState<{ row: PayoutRow; kind: 'reject' | 'settle' } | null>(
    null,
  );
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [runReason, setRunReason] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});

  const canRaise = allowed('partners.manage');
  const canDecide = allowed('funds.approve');
  const partnerWord = domain === 'agents' ? 'agent' : 'affiliate';

  const load = useCallback(async () => {
    if (!periodId) return;
    setLoading(true);
    try {
      const data = await api.get<PayoutRunPayload>(
        `/api/admin/${domain}/periods/${periodId}/payouts`,
      );
      setPayload(data);
      setFailed(false);
    } catch (error: unknown) {
      // The panel must not fall back to "nothing to pay" on a failed read —
      // that is a fact somebody would act on.
      setFailed(true);
      setPayload(null);
      showError({
        title: 'The payout run could not be read',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The period’s payouts did not come back. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setLoading(false);
    }
  }, [domain, periodId, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const close = () => {
    setPayload(null);
    setAnswering(null);
    setReason('');
    setReference('');
    setRunReason('');
    setFields({});
    setFailed(false);
    onClose();
  };

  const act = async (
    url: string,
    body: Record<string, unknown>,
    title: string,
  ): Promise<boolean> => {
    setBusy(true);
    try {
      const result = await api.post<{ message: string }>(url, body);
      toast(result.message);
      await load();
      onDone();
      return true;
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return false;
      }
      showError({
        title,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was recorded. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<PayoutRow>[] = [
    {
      key: 'partner',
      header: domain === 'agents' ? 'Agent' : 'Affiliate',
      render: (r) => (
        <>
          <span className="tabular">{r.partner_ref}</span>
          <div className="text-subtle" style={subLine}>
            {r.partner_name}
          </div>
        </>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      numeric: true,
      render: (r) => (
        <>
          <span className="tabular">{formatPesos(BigInt(r.amount))}</span>
          {r.is_early && (
            <div className="text-subtle" style={subLine}>
              early · {formatPesos(BigInt(r.handling_fee))} fee
            </div>
          )}
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <>
          <Tag tone={statusTone(r.status)}>{PAYOUT_STATUS_LABEL[r.status] ?? r.status}</Tag>
          <div className="text-subtle" style={subLine}>
            {/* Not "—": an unapproved payout is one WAITING for a checker, and
                that is a different fact from one nobody has looked at. */}
            {r.approved_by
              ? `approved by ${r.approved_by_name ?? 'an operator'}`
              : r.status === 'REJECTED'
                ? 'refused'
                : 'waiting for a second operator'}
          </div>
        </>
      ),
    },
    {
      key: 'destination',
      header: 'Destination',
      secondary: true,
      render: (r) =>
        r.payout_account_mask ? (
          <span className="tabular">
            {r.payout_method ?? 'account'} {r.payout_account_mask}
          </span>
        ) : (
          <span className="text-subtle">no account on file</span>
        ),
    },
    {
      key: 'act',
      header: 'Decision',
      render: (r) => {
        if (!canDecide) return <span className="text-subtle">funds.approve</span>;
        if (r.status === 'PAID') {
          return <span className="text-subtle">settled {r.paid_at ? stamp(r.paid_at) : ''}</span>;
        }
        if (r.status === 'REJECTED') return <span className="text-subtle">closed</span>;
        return (
          <div className="row">
            {!r.approved_by && (
              <Button
                variant="secondary"
                busy={busy}
                onClick={() =>
                  void act(
                    `/api/admin/${domain}/payouts/${r.id}/approve`,
                    {},
                    `Could not approve ${r.ref}`,
                  )
                }
              >
                Approve
              </Button>
            )}
            {r.approved_by && (
              <Button
                variant="primary"
                onClick={() => {
                  setFields({});
                  setReference('');
                  setAnswering({ row: r, kind: 'settle' });
                }}
              >
                Record settlement
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => {
                setFields({});
                setReason('');
                setAnswering({ row: r, kind: 'reject' });
              }}
            >
              Reject
            </Button>
          </div>
        );
      },
    },
  ];

  const period = payload?.period;
  const pending = payload?.pending;
  const payableCount = Number(pending?.payable ?? '0');

  return (
    <Dialog
      open={periodId !== null}
      onClose={close}
      wide
      title={period ? `Payout run · ${period.ref}` : 'Payout run'}
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            Close
          </Button>
          {canRaise && period?.status === 'CALCULATED' && payableCount > 0 && (
            <Button
              variant="primary"
              busy={busy}
              onClick={() =>
                void (async () => {
                  const done = await act(
                    `/api/admin/${domain}/periods/${period.id}/payouts`,
                    { reason: runReason },
                    `Could not raise the run for ${period.ref}`,
                  );
                  if (done) setRunReason('');
                })()
              }
            >
              Raise {formatCount(payableCount)} payout{payableCount === 1 ? '' : 's'}
            </Button>
          )}
        </>
      }
    >
      <div className="stack">
        {period && (
          <div className="stat-grid">
            <Stat
              label="Period"
              value={`${stamp(period.periodStart)} – ${stamp(period.periodEnd)}`}
            />
            <Stat
              label="Status"
              value={
                period.status === 'PAID'
                  ? 'Paid'
                  : period.status === 'CALCULATED'
                    ? 'Calculated'
                    : 'Open'
              }
            />
            <Stat
              label="Still to raise"
              value={formatPesos(BigInt(pending?.total ?? '0'))}
              delta={`${formatCount(payableCount)} ${partnerWord}${payableCount === 1 ? '' : 's'}`}
            />
          </div>
        )}

        {period?.status === 'OPEN' && (
          <div className="notice">
            <Icon name="warning-circle" />
            <div>
              {/* The closing job is commission.close (agents) or affiliate.commission.close. */}
              <strong>This period is still open.</strong> Its figures can still move as rounds
              settle, so nothing can be raised against it yet. A background job calculates and
              closes it once the window has ended.
            </div>
          </div>
        )}

        {pending && (Number(pending.held) > 0 || Number(pending.on_hold) > 0) && (
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              {Number(pending.held) > 0 && (
                <>
                  {formatCount(Number(pending.held))} {partnerWord}
                  {Number(pending.held) === 1 ? '' : 's'} accrued and <strong>held</strong> — a
                  suspended partner still earns, and is not paid.{' '}
                </>
              )}
              {Number(pending.on_hold) > 0 && (
                <>
                  {formatCount(Number(pending.on_hold))} inside the 24-hour payout hold that follows
                  a change of bank or wallet details.{' '}
                </>
              )}
              Neither is included in a run.
            </div>
          </div>
        )}

        {canRaise && period?.status === 'CALCULATED' && payableCount > 0 && (
          <Field
            label="Why this run is being raised"
            error={fields['reason']}
            hint="Recorded in the audit trail beside your name. Amounts come from each partner’s own period result — there is nothing to type."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={runReason}
                onChange={(e) => {
                  setRunReason(e.target.value);
                  setFields({});
                }}
                placeholder="Weekly settlement run for the closed period"
              />
            )}
          </Field>
        )}

        {loading && !payload ? (
          <div className="skeleton" style={{ height: '160px' }}>
            &nbsp;
          </div>
        ) : failed ? (
          <EmptyState icon="warning-circle" title="The payouts could not be read">
            The request did not come back, so this list is empty because nothing was read — not
            because nothing has been raised. The error dialog carries what the platform said.
          </EmptyState>
        ) : (payload?.payouts.length ?? 0) === 0 ? (
          <EmptyState icon="hand-coins" title="Nothing has been raised yet">
            {period?.status === 'CALCULATED'
              ? `${formatCount(payableCount)} ${partnerWord}${payableCount === 1 ? '' : 's'} in this period ${payableCount === 1 ? 'is' : 'are'} owed ${formatPesos(BigInt(pending?.total ?? '0'))}. Raising the run creates one scheduled payout each; each still needs a second operator to approve it.`
              : 'A run is raised once the period has been calculated.'}
          </EmptyState>
        ) : (
          <DataTable
            caption={`Payouts raised against this period, with amount, approval state, destination and the decision available`}
            columns={columns}
            rows={payload!.payouts}
            rowKey={(r) => r.id}
          />
        )}

        {answering && (
          <div className="notice notice-quiet">
            <Icon name={answering.kind === 'settle' ? 'bank' : 'prohibit'} />
            <div className="stack">
              {answering.kind === 'settle' ? (
                <>
                  <div>
                    <strong>
                      Recording a settlement of {formatPesos(BigInt(answering.row.amount))} to{' '}
                      {answering.row.partner_ref}.
                    </strong>{' '}
                    This platform integrates no payment provider and stubs none — it does not send
                    the money. Make the transfer at the bank or e-wallet, then record its reference
                    here so the row can be reconciled against the statement.
                  </div>
                  <Field
                    label="Bank or wallet reference"
                    error={fields['reference']}
                    hint="The reference of the transfer that was actually made."
                  >
                    {({ id, describedBy, invalid }) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        aria-invalid={invalid}
                        value={reference}
                        onChange={(e) => {
                          setReference(e.target.value);
                          setFields({});
                        }}
                        placeholder="BPI-99001"
                      />
                    )}
                  </Field>
                </>
              ) : (
                <>
                  <div>
                    <strong>
                      Refusing {answering.row.ref} — {formatPesos(BigInt(answering.row.amount))} to{' '}
                      {answering.row.partner_ref}.
                    </strong>{' '}
                    The commission stays accrued against the period; rejecting a payout does not
                    write it off.
                  </div>
                  <Field label="Why it is being refused" error={fields['reason']}>
                    {({ id, describedBy, invalid }) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        aria-invalid={invalid}
                        value={reason}
                        onChange={(e) => {
                          setReason(e.target.value);
                          setFields({});
                        }}
                        placeholder="The registered account does not match the verified name"
                      />
                    )}
                  </Field>
                </>
              )}
              <div className="row">
                <Button variant="secondary" onClick={() => setAnswering(null)}>
                  Cancel
                </Button>
                <Button
                  variant={answering.kind === 'settle' ? 'primary' : 'secondary'}
                  busy={busy}
                  onClick={() =>
                    void (async () => {
                      const done =
                        answering.kind === 'settle'
                          ? await act(
                              `/api/admin/${domain}/payouts/${answering.row.id}/release`,
                              { reference },
                              `Could not record ${answering.row.ref}`,
                            )
                          : await act(
                              `/api/admin/${domain}/payouts/${answering.row.id}/reject`,
                              { reason },
                              `Could not reject ${answering.row.ref}`,
                            );
                      if (done) setAnswering(null);
                    })()
                  }
                >
                  {answering.kind === 'settle' ? 'Record settlement' : 'Reject payout'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {(payload?.payouts ?? []).some((r) => r.is_early) && (
          <div className="notice notice-quiet">
            <Icon name="clock-countdown" />
            <div>
              <strong>An early payout is listed here and is not part of this run.</strong> It is
              drawn against future accrual rather than against a settled period, so it belongs to no
              period at all — it appears on every run screen until somebody answers it, which is the
              point: the partner asked, and until now nothing in the back office could reply.
            </div>
          </div>
        )}

        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            {payload?.disbursement ??
              'This platform records payouts. It does not move money: no payment provider is integrated, and recording a settlement records a transfer made at a bank or e-wallet outside it.'}{' '}
            {!canDecide && (
              <>
                Your role holds <code>partners.manage</code> and not <code>funds.approve</code>, so
                you may raise a run and not decide one — which is the maker–checker rule, not a
                missing button.
              </>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

const subLine = { marginTop: 'var(--space-1)', fontSize: 'var(--text-xs)' } as const;

const stamp = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-PH', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Manila',
  });

/**
 * The tone a payout's status carries.
 *
 * `accent` for settled and `outline` for refused, because those are the two
 * tones this design system actually has for "done" and "closed"; inventing a
 * colour here would be the first raw value in a codebase whose adherence check
 * exists to keep them out.
 */
const statusTone = (status: string): TagTone => {
  if (status === 'PAID') return 'accent';
  if (status === 'REJECTED') return 'outline';
  if (status === 'REQUESTED' || status === 'EARLY_REQUEST') return 'quiet';
  return 'neutral';
};
